import { useEffect, useRef, useState } from 'react';

import { Stop as StopIcon } from '@mui/icons-material';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useParams } from 'react-router';

import { useSnackbar } from '@/components/feedback/SnackbarProvider';
import {
  useGetExecutionLogsByGuidQuery,
  useGetExecutionMetadataByGuidQuery,
  useStopExecutionMutation,
} from '@/features/transformers/api/transformersApi';
import { LogViewer } from '@/features/transformers/components/LogViewer';
import { logger } from '@/utils/logger';

import { useRecipeEditor } from '../context/RecipeEditorContext';

interface RunningStatusPanelProps {
  tenantId: string;
}

const POLLING_INTERVAL_RUNNING = 1000;

function cleanGuid(guid: string): string {
  return guid.replace(/[\\/]+$/, '');
}

/**
 * Shows step-by-step execution progress while a recipe is running.
 * Polls at 1s intervals during execution, auto-stops when complete.
 * Uses LogViewer for auto-scroll, filtering, and search.
 */
export const RunningStatusPanel: React.FC<RunningStatusPanelProps> = ({ tenantId }) => {
  const { recipeId, appId } = useParams<{ recipeId: string; appId: string }>();
  const { state, dispatch } = useRecipeEditor();
  const { currentExternalExecutionId } = state;
  const hasRenamedRef = useRef(false);
  const [executionCompleted, setExecutionCompleted] = useState(false);

  const pollingInterval = executionCompleted ? 0 : POLLING_INTERVAL_RUNNING;
  const cleanedGuid = currentExternalExecutionId ? cleanGuid(currentExternalExecutionId) : '';

  // Poll execution metadata
  const {
    data: executionMetadata,
    refetch: refetchMetadata,
    isFetching: isFetchingMetadata,
  } = useGetExecutionMetadataByGuidQuery(
    { tenantId, executionGuid: cleanedGuid },
    {
      skip: !cleanedGuid || !tenantId,
      pollingInterval,
    }
  );

  const execution = executionMetadata?.[0];
  const isCompleted = execution?.completed === true;

  // Auto-stop polling when execution completes, and re-enable the Run button.
  // The Run button stays disabled (via isExecuting) for the whole running lifecycle
  // to prevent duplicate executions; completion is the point where it's safe again.
  useEffect(() => {
    if (isCompleted && !executionCompleted) {
      setExecutionCompleted(true);
      dispatch({ type: 'SET_IS_EXECUTING', payload: false });
    }
  }, [isCompleted, executionCompleted, dispatch]);

  // Poll execution steps at same interval
  const {
    data: executionSteps,
    refetch: refetchSteps,
    isFetching: isFetchingSteps,
  } = useGetExecutionLogsByGuidQuery(
    { tenantId, executionGuid: cleanedGuid },
    {
      skip: !cleanedGuid || !tenantId,
      pollingInterval,
    }
  );

  const [stopExecution] = useStopExecutionMutation();
  const { showSnackbar } = useSnackbar();

  // When execution completes, rename tab to "Results" and make it closeable
  useEffect(() => {
    if (isCompleted && !hasRenamedRef.current) {
      hasRenamedRef.current = true;
      const runningTab = state.bottomPanelTabs.find((t) => t.type === 'runningStatus');
      if (runningTab) {
        dispatch({ type: 'RENAME_BOTTOM_TAB', payload: { id: runningTab.id, title: 'Results' } });
        dispatch({
          type: 'SET_BOTTOM_TAB_CLOSEABLE',
          payload: { id: runningTab.id, closeable: true },
        });
      }
    }
  }, [isCompleted, state.bottomPanelTabs, dispatch]);

  const handleStop = async () => {
    if (!recipeId || !state.selectedEnvironment) return;
    try {
      await stopExecution({
        envId: state.selectedEnvironment,
        transId: recipeId,
      }).unwrap();
      // Re-enable the Run button now that the user has stopped the execution
      dispatch({ type: 'SET_IS_EXECUTING', payload: false });
      showSnackbar('Execution stopped', 'success');
    } catch (error) {
      logger.error('Failed to stop execution:', error);
      showSnackbar('Failed to stop execution', 'error');
    }
  };

  const handleRefresh = () => {
    refetchMetadata();
    refetchSteps();
  };

  // Waiting for execution metadata (first poll hasn't returned)
  if (!execution && currentExternalExecutionId) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, gap: 1 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary">
          Starting recipe execution...
        </Typography>
      </Box>
    );
  }

  if (!execution) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No execution data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Action bar: stop button + status summary */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        {!isCompleted && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<StopIcon />}
            onClick={handleStop}
            disabled={!state.selectedEnvironment}
            sx={{ fontSize: 12 }}
          >
            Stop
          </Button>
        )}

        {isCompleted && (
          <Typography
            variant="body2"
            fontWeight={600}
            color={execution.succeeded ? 'success.main' : 'error.main'}
            sx={{ fontSize: 12 }}
          >
            {execution.succeeded ? 'Completed successfully' : 'Completed with errors'}
            {execution.timer != null && ` — ${execution.timer.toFixed(2)}s`}
          </Typography>
        )}

        {execution.errorMessage && (
          <Typography
            variant="body2"
            color="error"
            sx={{
              flex: 1,
              fontSize: 11,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={execution.errorMessage}
          >
            {execution.errorMessage}
          </Typography>
        )}
      </Box>

      {/* Log viewer */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <LogViewer
          steps={executionSteps}
          isRunning={!isCompleted}
          onRefresh={handleRefresh}
          isFetching={isFetchingMetadata || isFetchingSteps}
          compact
          appId={appId}
          environmentId={state.selectedEnvironment || undefined}
        />
      </Box>
    </Box>
  );
};
